import { useState } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { formatTimeAgo } from '@/lib/utils';
import { Trash2, Eraser, ChevronDown, ChevronUp, CheckCircle2, Clock, Copy, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EventTypeBadge } from '@/components/EventTypeBadge';
import { RelayPropagationStatus } from '@/components/RelayPropagationStatus';
import { DeleteEventDialog } from '@/components/DeleteEventDialog';
import { NullifyEventDialog } from '@/components/NullifyEventDialog';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { isAddressableKind, isReplaceableKind } from '@/hooks/useTombstone';
import { nip19 } from 'nostr-tools';
import { getEventLabel } from '@/hooks/useTombstone';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface EventCardProps {
  event: NostrEvent;
  label: string;
  isDeleted: boolean;
  addrCoord?: string;
  deletionEventId?: string;
  onRefresh?: () => void;
}

function getEventTitle(event: NostrEvent): string | null {
  const title = event.tags.find(([name]) => name === 'title')?.[1];
  if (title) return title;

  const subject = event.tags.find(([name]) => name === 'subject')?.[1];
  if (subject) return subject;

  return null;
}

function getEventPreview(event: NostrEvent): string {
  const content = event.content?.trim();

  // Priority 1: Use 'd' tag for addressable events (most meaningful identifier)
  const dTag = event.tags.find(([name]) => name === 'd')?.[1];
  if (dTag) {
    if (event.kind === 30000) return `Follow Set: ${dTag}`;
    if (event.kind === 30002) return `Relay Set: ${dTag}`;
    if (event.kind === 30003) return `Bookmark Set: ${dTag}`;
    if (event.kind === 30004) return `Curation Set: ${dTag}`;
    if (event.kind === 30005) return `Video Set: ${dTag}`;
    if (event.kind === 30006) return `Picture Set: ${dTag}`;
    if (event.kind === 30007) return `Kind Mute Set: ${dTag}`;
    if (event.kind === 30008) return `Badge Set: ${dTag}`;
    if (event.kind === 30015) return `Interest Set: ${dTag}`;
    if (event.kind === 30030) return `Emoji Set: ${dTag}`;
    if (event.kind === 30267) return `App Curation Set: ${dTag}`;
    if (event.kind === 39089) return `Starter Pack: ${dTag}`;
    if (event.kind === 39092) return `Media Starter Pack: ${dTag}`;
    if (event.kind === 30402) return `Listing: ${dTag}`;
    if (event.kind === 30403) return `Draft Listing: ${dTag}`;
    if (event.kind === 30818) return `Wiki: ${dTag}`;
    if (event.kind === 30617) return `Git Repo: ${dTag}`;
    if (event.kind === 30618) return `Git Repo State: ${dTag}`;
    if (event.kind === 31924) return `Calendar: ${dTag}`;
    if (event.kind === 34235) return `Addressable Video: ${dTag}`;
    if (event.kind === 34236) return `Addressable Short Video: ${dTag}`;
    return `${getEventLabel(event.kind)}: ${dTag}`;
  }

  // Priority 2: Special cases for standard public lists (often have empty content)
  if (event.kind === 10002) return 'Public Relays';
  if (event.kind === 3) return 'Public Follows';
  if (event.kind === 10003) return 'Public Bookmarks';
  if (event.kind === 10000) return 'Mute List';
  if (event.kind === 10004) return 'Communities List';
  if (event.kind === 10005) return 'Public Chats List';
  if (event.kind === 10006) return 'Blocked Relays List';
  if (event.kind === 10007) return 'Search Relays List';
  if (event.kind === 10009) return 'User Groups';
  if (event.kind === 10015) return 'Interests List';
  if (event.kind === 10020) return 'Media Follows';
  if (event.kind === 10030) return 'User Emoji List';
  if (event.kind === 10050) return 'DM Relay List';
  if (event.kind === 10054) return 'Favorite Podcasts';
  if (event.kind === 10063) return 'User Server List';
  if (event.kind === 10064) return 'Authored Podcasts';
  if (event.kind === 30315) return 'User Status';
  if (event.kind === 10154) return 'Podcast Metadata';
  if (event.kind === 30009) return 'Badge Definition';

  // Priority 3: Show content if available
  if (content) {
    const truncated = content.slice(0, 120);
    return truncated.length < content.length ? truncated + '…' : truncated;
  }

  return '(empty)';
}

function getEventLink(event: NostrEvent, addrCoord?: string): string | null {
  try {
    if (addrCoord) {
      const parts = addrCoord.split(':');
      const encoded = nip19.naddrEncode({
        kind: parseInt(parts[0]),
        pubkey: parts[1],
        identifier: parts[2],
      });
      return encoded;
    }
    return nip19.noteEncode(event.id);
  } catch {
    return null;
  }
}

export function EventCard({
  event,
  label,
  isDeleted,
  addrCoord,
  deletionEventId,
  onRefresh,
}: EventCardProps) {
  const { toast } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [nullifyOpen, setNullifyOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const title = getEventTitle(event);
  const preview = getEventPreview(event);
  const createdAt = new Date(event.created_at * 1000);
  const timeAgo = formatTimeAgo(createdAt);
  
  // Nullify only makes sense for replaceable lists/sets and addressable events.
  // Regular short notes (kind 1) should only show the Delete button.
  const canNullify = isAddressableKind(event.kind) || isReplaceableKind(event.kind);
  const isNullified = isDeleted && (
    event.content.trim() === '' || 
    event.content.includes('Nullified by Tombstone')
  );
  const isAlreadyNullified = isNullified;
  
  const nip19ref = getEventLink(event, addrCoord);

  const handleCopyNip19 = async () => {
    if (!nip19ref) return;

    try {
      // Prefer Clipboard API but gracefully degrade in restricted environments (iframes, strict policies)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(nip19ref);
      } else {
        // Reliable fallback
        const textArea = document.createElement('textarea');
        textArea.value = nip19ref;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      toast({
        title: 'Copied!',
        description: nip19ref,
      });
    } catch (error: any) {
      console.error('Failed to copy:', error);
      
      // Show the value even on failure so user can copy manually
      toast({
        title: 'Copy failed',
        description: nip19ref,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <div className={cn(
        'group rounded-lg border bg-card transition-all duration-200',
        isDeleted
          ? 'border-muted-foreground/30 bg-muted/30 opacity-75'
          : 'border-accent/30 bg-accent/5 hover:border-accent/40 hover:bg-accent/10'
      )}>
        <div className="p-4">
          {/* Header row */}
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
               <div className="flex items-center gap-x-5 gap-y-1 text-xs flex-wrap">
                   <div className="flex items-center gap-1.5">
                     <span className="text-muted-foreground font-medium">Type</span>
                     <EventTypeBadge kind={event.kind} label={label} />
                   </div>


                   <div className="flex items-center gap-1.5">
                     <span className="text-muted-foreground font-medium">Status</span>
                     {isNullified ? (
                       <TooltipProvider>
                         <Tooltip>
                           <TooltipTrigger asChild>
                             <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                               <AlertTriangle className="w-3 h-3" />
                               Nullified
                             </span>
                           </TooltipTrigger>
                           <TooltipContent className="max-w-[280px]">
                             <p>Content has been emptied to overwrite this list on non-compliant relays (d-tag preserved)</p>
                           </TooltipContent>
                         </Tooltip>
                       </TooltipProvider>
                     ) : isDeleted ? (
                       <TooltipProvider>
                         <Tooltip>
                           <TooltipTrigger asChild>
                             <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-accent/10 text-accent border border-accent/20">
                               <CheckCircle2 className="w-3 h-3" />
                               Deleted
                             </span>
                           </TooltipTrigger>
                           <TooltipContent className="max-w-[260px]">
                             <p>Marked for deletion via NIP-09 kind 5 event (a-tag or e-tag)</p>
                           </TooltipContent>
                         </Tooltip>
                       </TooltipProvider>
                     ) : (
                       <TooltipProvider>
                         <Tooltip>
                           <TooltipTrigger asChild>
                             <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                               <CheckCircle className="w-3 h-3" />
                               Live
                             </span>
                           </TooltipTrigger>
                           <TooltipContent>
                             <p>This event is active on relays</p>
                           </TooltipContent>
                         </Tooltip>
                       </TooltipProvider>
                     )}
                   </div>


                 <span className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                   <Clock className="w-3 h-3" />
                   {timeAgo}
                 </span>
               </div>


              {/* Title or preview */}
              <div className="mt-2">
                {title ? (
                  <p className="text-sm font-medium text-foreground leading-snug">{title}</p>
                ) : (
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{preview}</p>
                )}
              </div>

               {/* Event ID */}
               <div className="mt-2 flex items-center gap-2">
                 <code className="text-[11px] text-muted-foreground/60 font-mono">
                   {event.id.slice(0, 12)}…{event.id.slice(-8)}
                 </code>
                 {nip19ref && (
                   <button
                     onClick={handleCopyNip19}
                     className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-accent transition-colors hover:bg-secondary/40 px-2 py-0.5 rounded"
                     title="Copy NIP-19 identifier"
                   >
                     <Copy className="w-3 h-3" />
                     Copy nip19
                   </button>
                 )}
               </div>
            </div>
          </div>

           {/* Actions row */}
           {(!isDeleted || isNullified) && (
             <div className="mt-3 pt-3 border-t border-border/50 flex items-center gap-2">

              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDeleteOpen(true)}
                className="h-7 px-3 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border border-transparent hover:border-destructive/20"
              >
                <Trash2 className="w-3 h-3 mr-1.5" />
                Delete (NIP-09)
              </Button>

                {canNullify && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setNullifyOpen(true)}
                    className="h-7 px-3 text-xs text-accent hover:text-accent hover:bg-accent/10 border border-transparent hover:border-accent/20"
                  >
                    <Eraser className="w-3 h-3 mr-1.5" />
                    {isAlreadyNullified ? 'Re-Nullify' : 'Nullify'}
                  </Button>
                )}


               <Button
                 size="sm"
                 variant="ghost"
                 onClick={() => setShowDetails(!showDetails)}
                 className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground ml-auto"
               >
                 Details
                 {showDetails ? (
                   <ChevronUp className="w-3 h-3 ml-1" />
                 ) : (
                   <ChevronDown className="w-3 h-3 ml-1" />
                 )}
               </Button>
             </div>
           )}

            {isDeleted && deletionEventId && !isNullified && (
              <div className="mt-3 pt-3 border-t border-border/50 flex items-center gap-2">

                <span className="text-xs text-muted-foreground">
                  Deletion request:{' '}
                  <code className="font-mono text-accent/70">
                    {deletionEventId.slice(0, 12)}…
                  </code>
                </span>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDetails(!showDetails)}
                  className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground ml-auto"
                >
                  Details
                  {showDetails ? (
                    <ChevronUp className="w-3 h-3 ml-1" />
                  ) : (
                    <ChevronDown className="w-3 h-3 ml-1" />
                  )}
                </Button>
             </div>
           )}

        </div>

         {/* Event details panel */}
         {showDetails && (
           <div className="px-4 pb-4 border-t border-border/40 pt-3 space-y-4">
             {isNullified && (
               <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3">
                 <p className="text-xs text-amber-500 font-medium mb-1">NULLIFIED EVENT</p>
                 <p className="text-xs text-muted-foreground">
                   This event was replaced with minimal metadata. Only the <code className="font-mono">d</code> tag 
                   (identity), <code className="font-mono">alt</code>, and <code className="font-mono">client</code> tags were preserved.
                   Content and all other fields were cleared.
                 </p>
               </div>
             )}

             {/* Raw event JSON */}
             <div>
               <div className="flex items-center justify-between mb-2">
                 <span className="text-xs font-medium text-muted-foreground">RAW EVENT</span>
                 <span className="text-[10px] text-muted-foreground font-mono">kind {event.kind}</span>
               </div>
               <pre className="bg-secondary/70 border border-border p-3 text-[10px] font-mono overflow-auto max-h-64 rounded text-foreground/90 whitespace-pre-wrap">
                 {JSON.stringify(event, null, 2)}
               </pre>
             </div>

             {/* Relay status */}
             <RelayPropagationStatus
               event={event}
               addrCoord={addrCoord}
             />
           </div>
         )}

      </div>

      <DeleteEventDialog
        event={event}
        label={label}
        addrCoord={addrCoord}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onRefresh}
      />

      <NullifyEventDialog
        event={event}
        label={label}
        open={nullifyOpen}
        onOpenChange={setNullifyOpen}
        onNullified={onRefresh}
      />
    </>
  );
}
